<template>
  <div
    class="d-flex flex-column ga-3"
    role="alert"
    data-cc="bekki-notice"
    :data-state="isCutShort ? 'cutShort' : 'connection'"
  >
    <div
      class="bekki-notice-card border border-secondary-softer border-opacity-100 d-flex flex-column pa-3 rounded-2xl"
    >
      <p class="text-subtitle-1">{{ copy.title }}</p>
      <p class="text-body-2">{{ copy.body }}</p>
      <v-btn
        v-if="isRetryShown"
        color="secondary"
        size="small"
        class="align-self-start"
        data-cc="bekki-notice-retry"
        @click="emit('retry')"
      >
        {{ t('widgets.bekki.error.connection.retry') }}
      </v-btn>
    </div>

    <p
      v-if="copy.footnote"
      class="bekki-notice-card border border-secondary-softer border-opacity-100 text-body-2 pa-3 rounded-2xl"
    >
      {{ copy.footnote }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';

import type { BekkiErrorCode } from '@/composables/api/bekki/types';

type NoticeKind =
  'cutShort' | 'rateLimited' | 'connection' | 'contextTooLong' | 'server';

interface NoticeCopy {
  title: string;
  body: string;
  footnote?: string;
}

interface Props {
  code: BekkiErrorCode;
  isRetryable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isRetryable: false
});

const emit = defineEmits<{
  retry: [];
}>();

const { t } = useI18n();

const NOTICE_KIND: Record<BekkiErrorCode, NoticeKind> = {
  generation_stopped: 'cutShort',
  rate_limited: 'rateLimited',
  connection: 'connection',
  context_too_long: 'contextTooLong',
  backend_unavailable: 'server',
  tool_failed: 'server',
  internal: 'server'
};

const noticeKind = computed((): NoticeKind => NOTICE_KIND[props.code]);

const isCutShort = computed((): boolean => noticeKind.value === 'cutShort');

const isRetryShown = computed(
  (): boolean =>
    props.isRetryable &&
    noticeKind.value !== 'cutShort' &&
    noticeKind.value !== 'contextTooLong'
);

const copy = computed((): NoticeCopy => {
  const copies: Record<NoticeKind, NoticeCopy> = {
    cutShort: {
      title: t('widgets.bekki.error.cutShort.title'),
      body: t('widgets.bekki.error.cutShort.hint')
    },
    rateLimited: {
      title: t('widgets.bekki.error.rateLimited.title'),
      body: t('widgets.bekki.error.rateLimited.body')
    },
    connection: {
      title: t('widgets.bekki.error.connection.title'),
      body: t('widgets.bekki.error.connection.body'),
      footnote: t('widgets.bekki.error.connection.footnote')
    },
    contextTooLong: {
      title: t('widgets.bekki.error.contextTooLong.title'),
      body: t('widgets.bekki.error.contextTooLong.body')
    },
    server: {
      title: t('widgets.bekki.error.server.title'),
      body: t('widgets.bekki.error.server.body'),
      footnote: t('widgets.bekki.error.connection.footnote')
    }
  };

  return copies[noticeKind.value];
});
</script>

<style scoped>
.bekki-notice-card {
  gap: 6px;
  background-color: rgba(var(--v-theme-secondary), var(--v-opacity-softer));
}
</style>
